import { repairAppearance } from './character/appearance.js';
import { World } from './world.js';

const LEGACY_STORAGE_KEY = 'worldcanvas.worlds.v1';
const INDEX_KEY = 'worldcanvas.worlds.v2.index';
const WORLD_KEY_PREFIX = 'worldcanvas.worlds.v2.world.';
export const WORLD_NAME_MAX_LENGTH = 32;

export function normalizeDayNightSave(dayNight) {
  if (!dayNight || typeof dayNight !== 'object' || !Number.isFinite(dayNight.elapsedMs)) return null;
  return { elapsedMs: dayNight.elapsedMs, paused: Boolean(dayNight.paused) };
}

export function repairWorldName(name) {
  return String(name ?? '').replace(/\s+/g, ' ').trim().slice(0, WORLD_NAME_MAX_LENGTH);
}

export function worldNameTaken(saves, name, exceptId) {
  const normalized = repairWorldName(name).toLowerCase();
  if (!normalized) return false;
  return saves.some((save) => save?.id !== exceptId && repairWorldName(save?.name).toLowerCase() === normalized);
}

export function createWorldSave(world, player, now = new Date(), dayNight = null, name = '') {
  const createdAt = now.toISOString();
  const clock = normalizeDayNightSave(dayNight);
  return {
    // Not the timestamp: two worlds created in the same millisecond must not
    // share an identity, or WorldStorage.save would overwrite one with the other.
    id: globalThis.crypto?.randomUUID?.() ?? `${createdAt}-${Math.random().toString(36).slice(2, 10)}`,
    name: repairWorldName(name) || `World ${formatWorldTimestamp(now)}`,
    createdAt,
    updatedAt: createdAt,
    world: world.serialize(),
    player: { x: player.x, y: player.y, appearance: repairAppearance(player.appearance) },
    ...(clock ? { dayNight: clock } : {}),
  };
}

export function formatWorldTimestamp(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function repairWorldSave(tileCatalog, save) {
  if (!save || typeof save !== 'object' || typeof save.id !== 'string'
    || typeof save.name !== 'string' || typeof save.createdAt !== 'string'
    || typeof save.updatedAt !== 'string' || !save.player
    || !Number.isFinite(save.player.x) || !Number.isFinite(save.player.y)) {
    throw new Error('Invalid saved world metadata');
  }

  const data = save.world;
  if (!data || data.version !== 1 || !Array.isArray(data.placements) || !Array.isArray(data.cells)
    || (data.creatures !== undefined && !Array.isArray(data.creatures))
    || (data.npcs !== undefined && !Array.isArray(data.npcs))) {
    throw new Error('Unsupported or invalid saved world data');
  }

  const { world, removedCells, removedCreatures, removedNpcs } = World.restore(tileCatalog, data);
  const repairedWorld = world.serialize();
  // Whatever failed to restore — including placements accepted at first and
  // evicted by a later cascade — shows up as a shorter surviving list.
  const removedTiles = Math.max(0, data.placements.length - repairedWorld.placements.length);
  const appearance = repairAppearance(save.player.appearance);
  const appearanceChanged = JSON.stringify(appearance) !== JSON.stringify(save.player.appearance);
  const clock = normalizeDayNightSave(save.dayNight);
  const dayNightInvalid = save.dayNight !== undefined && !clock;
  const worldChanged = removedTiles || removedCells || removedCreatures || removedNpcs;
  if (worldChanged || appearanceChanged || dayNightInvalid) {
    const repairedSave = {
      ...save,
      world: worldChanged ? repairedWorld : save.world,
      player: { ...save.player, appearance },
    };
    if (clock) repairedSave.dayNight = clock;
    else delete repairedSave.dayNight;
    return { world, save: repairedSave, removedTiles, removedCells, removedCreatures, removedNpcs };
  }
  return { world, save, removedTiles, removedCells, removedCreatures, removedNpcs };
}

/**
 * Drop every placement of `npcId` from every saved world. Called when an NPC
 * definition is deleted, so other worlds do not report it as incompatible on
 * their next load. Returns the number of saves rewritten.
 */
export function removeNpcFromSaves(storage, npcId) {
  let changed = 0;
  const saves = storage.list();
  for (const save of saves) {
    const npcs = save?.world?.npcs;
    if (!Array.isArray(npcs)) continue;
    const remaining = npcs.filter((npc) => npc?.npcId !== npcId);
    if (remaining.length === npcs.length) continue;
    storage.save({ ...save, world: { ...save.world, npcs: remaining } });
    changed += 1;
  }
  return changed;
}

/**
 * Each world lives under its own localStorage key, with a small id index.
 * The old format kept every world in one array under one key, which meant
 * saving world A re-serialized worlds B, C, D too — on every save.
 */
export class WorldStorage {
  constructor(storage = globalThis.localStorage) {
    this.storage = storage;
    this._migrated = false;
  }

  /** One-time, lazy: split a legacy single-blob store into per-world keys. */
  _migrate() {
    if (this._migrated) return;
    this._migrated = true;
    const legacy = this.storage.getItem(LEGACY_STORAGE_KEY);
    if (legacy === null) return;
    try {
      const saves = JSON.parse(legacy);
      if (Array.isArray(saves)) {
        const existing = new Set(this._readIndex());
        // Never clobber a world already migrated — if a previous migration was
        // interrupted before removing the blob, the per-world copy is newer.
        for (const save of saves) {
          if (save && typeof save.id === 'string' && !existing.has(save.id)) this._writeWorld(save);
        }
      }
    } catch {
      // A corrupt legacy blob holds nothing recoverable.
    }
    this.storage.removeItem?.(LEGACY_STORAGE_KEY);
  }

  _readIndex() {
    try {
      const ids = JSON.parse(this.storage.getItem(INDEX_KEY) ?? '[]');
      return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : [];
    } catch {
      return [];
    }
  }

  _writeWorld(save) {
    const ids = this._readIndex();
    if (!ids.includes(save.id)) {
      this.storage.setItem(INDEX_KEY, JSON.stringify([...ids, save.id]));
    }
    this.storage.setItem(WORLD_KEY_PREFIX + save.id, JSON.stringify(save));
  }

  _readWorld(id) {
    try {
      const save = JSON.parse(this.storage.getItem(WORLD_KEY_PREFIX + id) ?? 'null');
      return save && typeof save === 'object' ? save : null;
    } catch {
      return null;
    }
  }

  list() {
    this._migrate();
    const saves = [];
    for (const id of this._readIndex()) {
      const save = this._readWorld(id);
      if (save) saves.push(save);
    }
    return saves;
  }

  latest() {
    return this.list().sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0] ?? null;
  }

  get(id) {
    this._migrate();
    return typeof id === 'string' ? this._readWorld(id) : null;
  }

  save(save) {
    this._migrate();
    this._writeWorld(save);
  }

  /** Remove a saved world. Returns whether anything was deleted. */
  delete(id) {
    this._migrate();
    const ids = this._readIndex();
    const remaining = ids.filter((candidate) => candidate !== id);
    if (remaining.length === ids.length) return false;
    this.storage.setItem(INDEX_KEY, JSON.stringify(remaining));
    this.storage.removeItem?.(WORLD_KEY_PREFIX + id);
    return true;
  }
}
